import React, { useRef, useEffect } from 'react'
import cx from 'classnames'
import { bool, func, node, string } from 'prop-types'

import styles from './width-observed-element.module.css'

const propTypes = {
    children: node,
    className: string,
    onWidthChanged: func,
    visible: bool
}

const defaultProps = {
    onWidthChanged: () => {},
    visible: true
}

/**
 * A wrapper element to report changes of children width (resize) to the parent component.
 *
 * @param {ReactElement} children		- The children to be observed
 * @param {string} className			- Additional classname
 * @param {boolean} visible				- Hide the component if wanted
 * @param {function} onWidthChanged		- Callback to report width changes
 * @return {ReactElement}
 */
export function WidthObservedElement({ children, className, onWidthChanged, visible }) {
    const wrapper = useRef()

    useEffect(() => {
        if (typeof window.ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(entries => {
                const width =
                    entries[0]?.borderBoxSize?.[0]?.inlineSize || wrapper.current.getBoundingClientRect().width
                onWidthChanged(width)
            })
            observer.observe(wrapper.current)

            // Report width initially. This is needed for children components that render to null
            // and thus will never trigger a resize event.
            onWidthChanged(wrapper.current?.getBoundingClientRect().width || 0)

            return () => {
                observer.disconnect()
            }
        }
    }, [onWidthChanged])

    return (
        <div
            className={cx(className, 'width-observed-element', {
                [styles['hidden']]: !visible
            })}
            ref={wrapper}
        >
            {children}
        </div>
    )
}

WidthObservedElement.propTypes = propTypes
WidthObservedElement.defaultProps = defaultProps
